/**
 * SB5-B.1 — Contract state chip, on D81's slot recipe.
 *
 * Per Christian's locked colour matrix in `02-page-contracts.md` §Table
 * column 8, and the slot each band now means:
 *   white  ← CREATED, SCHEDULED             → neutral   (inert · not started)
 *   yellow ← EXECUTION, ACCOUNTING, PAYMENT → attention (needs a human)
 *   green  ← COMPLETED                      → success
 *   red    ← WITHDRAWN, REJECTED            → critical  (terminal · blocking)
 *
 * The state → band matrix is UNCHANGED; only what a band resolves to moved.
 * Mirrors `ProjectStatusChip` shape — same `data-state` + `data-color`
 * test hooks so acceptance specs can assert bands without scraping CSS.
 */
import { type ReactElement } from "react";
import { Chip } from "@mui/material";
import { statusChipStyle, type StatusSlot } from "@aeriosiq/ui";
import type { ContractState } from "./types.js";

type ColorBand = "white" | "yellow" | "green" | "red";

/** Band → D81 slot. The band is the domain vocabulary `data-color` publishes;
 *  the slot is the MEANING that carries the colour. */
const SLOT_BY_BAND: Record<ColorBand, StatusSlot> = {
  white: "neutral",
  yellow: "attention",
  green: "success",
  red: "critical",
};

const STATE_COLOR: Record<ContractState, ColorBand> = {
  CREATED: "white",
  SCHEDULED: "white",
  EXECUTION: "yellow",
  ACCOUNTING: "yellow",
  PAYMENT: "yellow",
  COMPLETED: "green",
  WITHDRAWN: "red",
  REJECTED: "red",
};

const STATE_LABEL: Record<ContractState, string> = {
  CREATED: "Created",
  SCHEDULED: "Scheduled",
  EXECUTION: "Execution",
  ACCOUNTING: "Accounting",
  PAYMENT: "Payment",
  COMPLETED: "Completed",
  WITHDRAWN: "Withdrawn",
  REJECTED: "Rejected",
};

export function contractStateLabel(state: ContractState): string {
  return STATE_LABEL[state];
}

export function ContractStatusChip({
  state,
}: {
  state: ContractState;
}): ReactElement {
  const color = STATE_COLOR[state];
  return (
    <Chip
      size="small"
      label={STATE_LABEL[state]}
      data-state={state}
      data-color={color}
      sx={(t) => {
        // The hue owns the fill and the keyline; the label stays neutral. Every
        // band now carries a keyline, not just the old near-transparent white.
        const chip = statusChipStyle(t, SLOT_BY_BAND[color]);
        return {
          backgroundColor: chip.backgroundColor,
          color: chip.color,
        };
      }}
    />
  );
}
